"use client";

import { c as _c } from "react/compiler-runtime";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import useMergedRef from "@react-hook/merged-ref";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { tv } from "tailwind-variants";
import { useDebouncedCallback } from "use-debounce";
import { disabledVariants } from "../../styles";
import { tooltipVariants } from "../../styles/utils/tooltip";
import { classNames } from "../../utils";
import { HydrationGuard } from "../HydrationGuard";
import { Item, ItemAvatar, ItemContent, ItemDescription, ItemTitle } from "../Item";
import { Separator } from "../Separator";
const DropdownContext = createContext({
  size: "sm"
});
function Dropdown(t0) {
  const $ = _c(51);
  let children;
  let className;
  let content;
  let defaultOpen;
  let forceMount;
  let onOpenChange;
  let open;
  let overrides;
  let props;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      content,
      open,
      defaultOpen,
      onOpenChange,
      children,
      className,
      overrides,
      forceMount,
      trigger: t1,
      size: t2,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = content;
    $[4] = defaultOpen;
    $[5] = forceMount;
    $[6] = onOpenChange;
    $[7] = open;
    $[8] = overrides;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
  } else {
    children = $[1];
    className = $[2];
    content = $[3];
    defaultOpen = $[4];
    forceMount = $[5];
    onOpenChange = $[6];
    open = $[7];
    overrides = $[8];
    props = $[9];
    ref = $[10];
    t1 = $[11];
    t2 = $[12];
  }
  const trigger = t1 === undefined ? "click" : t1;
  const size = t2 === undefined ? "sm" : t2;
  let t3;
  let t4;
  if ($[13] !== size) {
    t4 = {
      size
    };
    $[13] = size;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  t3 = t4;
  const contextValue = t3;
  const triggerRef = useRef(null);
  const mergedRef = useMergedRef(ref, triggerRef);
  const [openedState, setOpenedState] = useState(defaultOpen ?? open);
  const resolvedOpenState = open === undefined ? openedState : open;
  let t5;
  if ($[15] !== onOpenChange) {
    t5 = open_0 => {
      setOpenedState(open_0);
      onOpenChange?.(open_0);
    };
    $[15] = onOpenChange;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleOnOpenChange = t5;
  const debouncedHandleOnOpenChange = useDebouncedCallback(handleOnOpenChange, 100);
  let t6;
  if ($[17] !== debouncedHandleOnOpenChange || $[18] !== trigger) {
    t6 = trigger === "hover" ? () => {
      debouncedHandleOnOpenChange(true);
    } : undefined;
    $[17] = debouncedHandleOnOpenChange;
    $[18] = trigger;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const hoverTriggerOnPointerEnter = t6;
  let t7;
  if ($[20] !== debouncedHandleOnOpenChange || $[21] !== trigger) {
    t7 = trigger === "hover" ? () => {
      debouncedHandleOnOpenChange(false);
    } : undefined;
    $[20] = debouncedHandleOnOpenChange;
    $[21] = trigger;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const hoverTriggeronPointerLeave = t7;
  const t8 = trigger !== "hover";
  let t9;
  if ($[23] !== className) {
    t9 = classNames("focus-visible:outline-hidden", className);
    $[23] = className;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== children || $[26] !== hoverTriggerOnPointerEnter || $[27] !== hoverTriggeronPointerLeave || $[28] !== mergedRef || $[29] !== t9) {
    t10 = <DropdownPrimitive.Trigger asChild={true} className={t9} onPointerEnter={hoverTriggerOnPointerEnter} onPointerLeave={hoverTriggeronPointerLeave} ref={mergedRef}>{children}</DropdownPrimitive.Trigger>;
    $[25] = children;
    $[26] = hoverTriggerOnPointerEnter;
    $[27] = hoverTriggeronPointerLeave;
    $[28] = mergedRef;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== content || $[32] !== hoverTriggerOnPointerEnter || $[33] !== hoverTriggeronPointerLeave || $[34] !== overrides?.Content?.className || $[35] !== props || $[36] !== trigger) {
    t11 = content && <DropdownPrimitive.Content className={classNames(tooltipVariants({
      animation: trigger === "hover" ? "slide-out" : "fade-in"
    }), "rounded-lg p-1", "overflow-auto font-normal", "w-[var(--radix-dropdown-menu-trigger-width)]", "max-h-[450px] min-w-max", overrides?.Content?.className)} onCloseAutoFocus={trigger === "hover" ? _temp : undefined} onPointerEnter={hoverTriggerOnPointerEnter} onPointerLeave={hoverTriggeronPointerLeave} sideOffset={4} {...props}>{content}</DropdownPrimitive.Content>;
    $[31] = content;
    $[32] = hoverTriggerOnPointerEnter;
    $[33] = hoverTriggeronPointerLeave;
    $[34] = overrides?.Content?.className;
    $[35] = props;
    $[36] = trigger;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== forceMount || $[39] !== t11) {
    t12 = <HydrationGuard><DropdownPrimitive.Portal forceMount={forceMount}>{t11}</DropdownPrimitive.Portal></HydrationGuard>;
    $[38] = forceMount;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== defaultOpen || $[42] !== handleOnOpenChange || $[43] !== resolvedOpenState || $[44] !== t10 || $[45] !== t12 || $[46] !== t8) {
    t13 = <DropdownPrimitive.Root defaultOpen={defaultOpen} modal={t8} onOpenChange={handleOnOpenChange} open={resolvedOpenState}>{t10}{t12}</DropdownPrimitive.Root>;
    $[41] = defaultOpen;
    $[42] = handleOnOpenChange;
    $[43] = resolvedOpenState;
    $[44] = t10;
    $[45] = t12;
    $[46] = t8;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== contextValue || $[49] !== t13) {
    t14 = <DropdownContext.Provider value={contextValue}>{t13}</DropdownContext.Provider>;
    $[48] = contextValue;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
}
function _temp(e) {
  return e.preventDefault();
}
const dropdownItemVariants = tv({
  base: classNames("rounded border-0 focus:bg-bg-additional-1 focus:outline-hidden", disabledVariants()),
  variants: {
    size: {
      sm: "gap-2.5 px-2 py-1.5",
      md: "px-3 py-4"
    }
  }
});
function DropdownItem(t0) {
  const $ = _c(24);
  let asChild;
  let children;
  let className;
  let disabled;
  let onSelect;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      asChild,
      children,
      className,
      disabled,
      closeOnSelect: t1,
      onSelect,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = disabled;
    $[5] = onSelect;
    $[6] = rest;
    $[7] = t1;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    disabled = $[4];
    onSelect = $[5];
    rest = $[6];
    t1 = $[7];
  }
  const closeOnSelect = t1 === undefined ? true : t1;
  const {
    size
  } = useContext(DropdownContext);
  let t2;
  if ($[8] !== closeOnSelect || $[9] !== onSelect) {
    t2 = event => {
      if (!closeOnSelect) {
        event.preventDefault();
      }
      onSelect?.(event);
    };
    $[8] = closeOnSelect;
    $[9] = onSelect;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== className || $[12] !== size) {
    t3 = classNames(dropdownItemVariants({
      size
    }), className);
    $[11] = className;
    $[12] = size;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== asChild || $[15] !== children || $[16] !== disabled || $[17] !== t3) {
    t4 = <Item asChild={asChild} className={t3} disabled={disabled} interactive={true}>{children}</Item>;
    $[14] = asChild;
    $[15] = children;
    $[16] = disabled;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== disabled || $[20] !== rest || $[21] !== t2 || $[22] !== t4) {
    t5 = <DropdownPrimitive.Item {...rest} asChild={true} disabled={disabled} onPointerUp={_temp2} onSelect={t2}>{t4}</DropdownPrimitive.Item>;
    $[19] = disabled;
    $[20] = rest;
    $[21] = t2;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
}
function _temp2(e) {
  return e.preventDefault();
}
const dropdownAvatarVariants = tv({
  variants: {
    size: {
      sm: "size-4",
      md: ""
    }
  }
});
export const DropdownItemAvatar = t0 => {
  const $ = _c(9);
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  const {
    size
  } = useContext(DropdownContext);
  let t1;
  if ($[3] !== className || $[4] !== size) {
    t1 = classNames(dropdownAvatarVariants({
      size
    }), className);
    $[3] = className;
    $[4] = size;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest || $[7] !== t1) {
    t2 = <ItemAvatar className={t1} {...rest} />;
    $[6] = rest;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const DropdownItemTitle = props => {
  const $ = _c(3);
  const {
    size
  } = useContext(DropdownContext);
  const t0 = size === "sm" ? "regular" : "semibold";
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = <ItemTitle weight={t0} {...props} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const DropdownItemDescription = ItemDescription;
export const DropdownItemContent = ItemContent;
export function DropdownSeparator(t0) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = classNames("my-0.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== rest || $[6] !== t1) {
    t2 = <Separator className={t1} {...rest} />;
    $[5] = rest;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Displays a menu to the user — such as a set of actions or functions — triggered by a button.
 */
export { Dropdown, DropdownItem };